import { Copy, ShoppingCart, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import CustomCheckbox from '../../../../../../components/ui/checkboxes/CustomCheckbox';
import { formatCurrency } from '../../../../../../utils/formatters';
import NumberInput from '../../../../../../components/ui/inputs/NumberInput';
import SimpleInfo from '../components/SimpleInfo';
import CustomButton from '../../../../../../components/ui/buttons/CustomButton';
import { toBlob } from 'html-to-image';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';

const GRID = '30px 100px 70px 1fr 1fr 1fr 100px';

const situationColor = (situation) => {
  switch (situation) {
    case 'wc':
      return 'text-greenBright';
    case 'w':
      return 'text-purpleBright';
    case 'c':
      return 'text-orangeBright';
    default:
      return '';
  }
};

const PurchaseRow = ({ position, idx, isSelected, onToggle, onQuantityToBuyChange }) => {
  const isBlueRow = idx % 2 === 0;
  const { priceUnavailable } = position;

  return (
    <div className={`${isBlueRow ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
      <div className="grid items-center gap-4 px-3 py-3 text-sm text-blueDarker" style={{ gridTemplateColumns: GRID }}>
        <CustomCheckbox checked={isSelected} disabled={priceUnavailable} onChange={() => onToggle(position.id)} />

        <div className={`truncate font-semibold ${situationColor(position.situation)}`} title={position.ticker}>
          {position.ticker}
        </div>

        <div className="truncate" title={position.tSubtype}>
          {position.tSubtype}
        </div>

        {priceUnavailable ? (
          <div className="truncate text-xs text-blueSteel col-span-3" title="Preço de mercado não sincronizado">
            Preço indisponível
          </div>
        ) : (
          <>
            <div className="truncate" title={formatCurrency(position.marketPrice)}>
              {formatCurrency(position.marketPrice)}
            </div>

            <div className="truncate" title={formatCurrency(position.idealAmount)}>
              {formatCurrency(position.idealAmount)}
            </div>

            <div className="truncate font-semibold" title={formatCurrency(position.totalPurchase)}>
              {formatCurrency(position.totalPurchase)}
            </div>
          </>
        )}

        <div>
          <NumberInput
            name={`quantity-to-buy-${position.id}`}
            value={position.quantityToBuy}
            onChange={(e) => onQuantityToBuyChange(position.id, e.target.value)}
            py="py-2"
            textSize="text-sm"
            decimalScale={0}
            disabled={priceUnavailable}
          />
        </div>
      </div>
    </div>
  );
};

const CopiedTooltip = ({ visible, message }) => {
  if (!visible) return null;

  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 transform mt-2 bg-blueDarker text-white text-xs px-3 py-1 rounded z-10 whitespace-nowrap">
      {message}
      <div className="absolute -top-1 left-1/2 -translate-x-1 transform w-2 h-2 bg-blueDarker rotate-45"></div>
    </div>
  );
};

const PurchasePlanning = ({ equityPositions = [], accountNumber, clientName }) => {
  const [planningResults, setPlanningResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCopyingOutput, setIsCopyingOutput] = useState(false);
  const [copiedOutput, setCopiedOutput] = useState(null);

  const { addToast } = useToast();

  const visibleIds = useMemo(() => {
    return planningResults.map((position) => position.id);
  }, [planningResults]);

  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      }

      return [...prev, id];
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const allVisibleSelected = visibleIds.every((id) => prev.includes(id));

      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }

      return [...new Set([...prev, ...visibleIds])];
    });
  };

  const handleQuantityToBuyChange = (positionId, value) => {
    const parsedValue = value || 0;

    setPlanningResults((prev) =>
      prev.map((position) => {
        if (position.id !== positionId) return position;

        return {
          ...position,
          quantityToBuy: parsedValue,
          totalPurchase: parsedValue * Number(position.marketPrice || 0),
        };
      })
    );
  };

  useEffect(() => {
    // Sugere a compra de qualquer ativo recomendado abaixo do ideal (não possui ou está sub-alocado).
    const results = equityPositions
      // balancing (delta em R$) não depende de market_price, então um ativo recomendado sem preço
      // sincronizado ainda entra na lista — balancingQuantity vem nil nesse caso (ver equities_controller.rb).
      .filter((position) => Number(position.balancing) > 0)
      .map((position) => {
        const priceUnavailable = position.product.marketPrice == null;
        const marketPrice = Number(position.product.marketPrice || 0);
        const quantityToBuy = priceUnavailable
          ? 0
          : Math.max(0, Math.floor(Number(position.balancingQuantity || 0)));

        return {
          id: position.id,
          ticker: position.product.ticker,
          situation: position.situation,
          subtype: position.product.subtype,
          tSubtype: position.product.tSubtype,
          priceUnavailable,
          marketPrice,
          idealAmount: Number(position.idealAmount || 0),
          currentAmount: Number(position.amount || 0),
          quantityToBuy,
          totalPurchase: quantityToBuy * marketPrice,
        };
      });

    setPlanningResults(results);
    setSelectedIds(results.filter((r) => r.quantityToBuy > 0).map((r) => r.id));
  }, [equityPositions]);

  const summaries = useMemo(() => {
    const totalPurchaseValue = planningResults.reduce((sum, item) => sum + item.totalPurchase, 0);

    return { totalPurchaseValue };
  }, [planningResults]);

  const showCopiedFeedback = (outputType) => {
    setCopiedOutput(outputType);

    setTimeout(() => {
      setCopiedOutput(null);
    }, 2200);
  };

  const showCopyError = (error, fallbackMessage = 'Erro ao copiar conteúdo.') => {
    console.error(fallbackMessage, error);

    addToast({
      type: 'error',
      message: error?.message || fallbackMessage,
    });
  };

  const showNoRowsError = () => {
    addToast({
      type: 'error',
      message: 'Nenhum ativo selecionado para copiar.',
    });
  };

  const getRowsToCopy = () => {
    return planningResults.filter((position) => {
      return selectedIds.includes(position.id) && Number(position.quantityToBuy || 0) > 0;
    });
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const formatCurrencyHtml = (value) => {
    return escapeHtml(formatCurrency(Number(value || 0)));
  };

  const buildPurchaseOrderTableHtml = (rows) => {
    const safeClientName = escapeHtml(clientName || 'Cliente');
    const safeAccountNumber = escapeHtml(accountNumber || '');

    const tableRows = rows
      .map((position) => {
        const ticker = escapeHtml(position.ticker || '');
        const quantity = Math.floor(Number(position.quantityToBuy || 0));

        return `
        <tr>
          <td style="padding: 16px 22px; border-bottom: 1px solid #DDECEF; font-weight: 700; color: #123B5A; text-align: left;">
            ${ticker}
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            C
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${quantity}
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            M
          </td>
          <td style="padding: 16px 18px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${safeAccountNumber}
          </td>
          <td style="padding: 16px 22px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            DIA
          </td>
        </tr>
      `;
      })
      .join('');

    return `
    <div style="width: 720px; background: #FFFFFF; font-family: Arial, Helvetica, sans-serif; color: #123B5A;">
      <div style="padding: 18px 16px 14px 16px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="vertical-align: middle; text-align: left;">
              <img src="https://res.cloudinary.com/rio-claro-investimentos/image/upload/mqw2l1ufnmvkqvkqtfjb9tb0a0zf.png" alt="Rio Claro Investimentos" style="display:block;width:160px;height:auto;" />
            </td>

            <td style="vertical-align: middle; text-align: right;">
              <span
                style="
                  display: inline-block;
                  width: 22px;
                  height: 22px;
                  background-color: #39B8BE;
                  -webkit-mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  vertical-align: middle;
                  margin-right: 8px;
                "
              ></span>
              <span style="font-size: 19px; font-weight: 700; color: #123B5A; vertical-align: middle;">
                ${safeClientName}
              </span>
            </td>
          </tr>
        </table>
      </div>

      <div style="margin: 0 16px; border: 1px solid #DDECEF; border-radius: 12px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 15px;">
          <colgroup>
            <col style="width: 150px;" />
            <col style="width: 70px;" />
            <col style="width: 90px;" />
            <col style="width: 90px;" />
            <col style="width: 180px;" />
            <col style="width: 110px;" />
          </colgroup>
          <thead>
            <tr>
              <th colspan="6" style="background: #3CB4B9; color: #FFFFFF; padding: 16px 12px; font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 0.2px;">
                ORDEM SIMPLES DE COMPRA E VENDA *
              </th>
            </tr>

            <tr>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 22px; font-size: 15px; font-weight: 700; text-align: left;">
                Ativo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                C/V
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Qtd.
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Preço
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 18px; font-size: 15px; font-weight: 700; text-align: center;">
                Conta
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 14px 22px; font-size: 15px; font-weight: 700; text-align: center;">
                Validade
              </th>
            </tr>
          </thead>

          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>

      <div style="padding: 18px 42px 10px 42px; font-size: 13px; line-height: 1.4; color: #4D6A7F; font-style: italic;">
        * Os dados apresentados são estimativas e não refletem valores reais de execução.
      </div>
    </div>
  `;
  };

  const buildPurchaseOrderTableText = (rows) => {
    const header = ['Ativo', 'C/V', 'Qtd.', 'Preço', 'Conta', 'Validade'].join('\t');

    const body = rows
      .map((position) => {
        return [
          position.ticker || '',
          'C',
          Math.floor(Number(position.quantityToBuy || 0)),
          'M',
          accountNumber || '',
          'DIA',
        ].join('\t');
      })
      .join('\n');

    return `ORDEM SIMPLES DE COMPRA E VENDA\n${header}\n${body}`;
  };

  const copyPurchaseOrderTable = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildPurchaseOrderTableHtml(rows);
    const text = buildPurchaseOrderTableText(rows);

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(text);
      }

      showCopiedFeedback('table');
    } catch (error) {
      showCopyError(error, 'Erro ao copiar tabela.');
    } finally {
      setIsCopyingOutput(false);
    }
  };

  const buildPurchasePlanningReportHtml = (rows) => {
    const safeClientName = escapeHtml(clientName || 'Cliente');

    const totalPurchaseValue = rows.reduce((sum, position) => {
      return sum + Number(position.totalPurchase || 0);
    }, 0);

    const tableRows = rows
      .map((position) => {
        const ticker = escapeHtml(position.ticker || '');
        const type = escapeHtml(position.tSubtype || position.subtype || '');
        const quantity = Math.floor(Number(position.quantityToBuy || 0));
        const marketPrice = formatCurrencyHtml(position.marketPrice);
        const totalPurchase = formatCurrencyHtml(position.totalPurchase);

        return `
        <tr>
          <td style="padding: 14px 16px; border-bottom: 1px solid #DDECEF; font-weight: 700; color: #123B5A; text-align: left;">
            ${ticker}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${type}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: center;">
            ${quantity}
          </td>
          <td style="padding: 14px 12px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: right; white-space: nowrap;">
            ${marketPrice}
          </td>
          <td style="padding: 14px 16px; border-bottom: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700; white-space: nowrap;">
            ${totalPurchase}
          </td>
        </tr>
      `;
      })
      .join('');

    return `
    <div style="width: 860px; background: #FFFFFF; font-family: Arial, Helvetica, sans-serif; color: #123B5A;">
      <div style="padding: 18px 16px 14px 16px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="vertical-align: middle; text-align: left;">
              <img src="https://res.cloudinary.com/rio-claro-investimentos/image/upload/mqw2l1ufnmvkqvkqtfjb9tb0a0zf.png" alt="Rio Claro Investimentos" style="display:block;width:160px;height:auto;" />
            </td>

            <td style="vertical-align: middle; text-align: right;">
              <span
                style="
                  display: inline-block;
                  width: 22px;
                  height: 22px;
                  background-color: #39B8BE;
                  -webkit-mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  mask: url('https://res.cloudinary.com/rio-claro-investimentos/image/upload/5i26i5ohlh362ug787phl9d8wngd.png') center / contain no-repeat;
                  vertical-align: middle;
                  margin-right: 8px;
                "
              ></span>
              <span style="font-size: 19px; font-weight: 700; color: #123B5A; vertical-align: middle;">
                ${safeClientName}
              </span>
            </td>
          </tr>
        </table>
      </div>

      <div style="margin: 0 16px 14px 16px; border: 1px solid #DDECEF; border-radius: 12px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 14px;">
          <colgroup>
            <col style="width: 160px;" />
            <col style="width: 110px;" />
            <col style="width: 90px;" />
            <col style="width: 160px;" />
            <col style="width: 180px;" />
          </colgroup>
          <thead>
            <tr>
              <th colspan="5" style="background: #3CB4B9; color: #FFFFFF; padding: 16px 12px; font-size: 16px; font-weight: 700; text-align: center; letter-spacing: 0.2px;">
                RELATÓRIO DE PLANEJAMENTO DE COMPRAS*
              </th>
            </tr>

            <tr>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 16px; font-size: 14px; font-weight: 700; text-align: left;">
                Ativo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: center;">
                Tipo
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: center;">
                Qtd.
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 12px; font-size: 14px; font-weight: 700; text-align: right;">
                Pr Atual
              </th>
              <th style="background: #E8F2F3; color: #123B5A; padding: 13px 16px; font-size: 14px; font-weight: 700; text-align: right;">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            ${tableRows}

            <tr>
              <td colspan="4" style="padding: 15px 16px; background: #F6FAFB; border-top: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700;">
                Total
              </td>
              <td style="padding: 15px 16px; background: #F6FAFB; border-top: 1px solid #DDECEF; color: #123B5A; text-align: right; font-weight: 700; white-space: nowrap;">
                ${formatCurrencyHtml(totalPurchaseValue)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style="padding: 18px 42px 10px 42px; font-size: 13px; line-height: 1.4; color: #4D6A7F; font-style: italic;">
        * Os valores apresentados são estimativas e não refletem valores reais de execução.
      </div>
    </div>
  `;
  };

  const buildPurchasePlanningReportText = (rows) => {
    const totalPurchaseValue = rows.reduce((sum, position) => {
      return sum + Number(position.totalPurchase || 0);
    }, 0);

    const header = ['Ativo', 'Tipo', 'Qtd.', 'Pr Atual', 'Total'].join('\t');

    const body = rows
      .map((position) => {
        return [
          position.ticker || '',
          position.tSubtype || position.subtype || '',
          Math.floor(Number(position.quantityToBuy || 0)),
          formatCurrency(Number(position.marketPrice || 0)),
          formatCurrency(Number(position.totalPurchase || 0)),
        ].join('\t');
      })
      .join('\n');

    const totalRow = ['Total', '', '', '', formatCurrency(totalPurchaseValue)].join('\t');

    return [
      'RELATÓRIO DE PLANEJAMENTO DE COMPRAS*',
      clientName || 'Cliente',
      '',
      header,
      body,
      totalRow,
      '',
      `Total de compras: ${formatCurrency(totalPurchaseValue)}`,
      '',
      '* Os valores apresentados são estimativas e não refletem valores reais de execução.',
    ].join('\n');
  };

  const copyPurchasePlanningReport = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildPurchasePlanningReportHtml(rows);
    const text = buildPurchasePlanningReportText(rows);

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });

        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(text);
      }

      showCopiedFeedback('report_text');
    } catch (error) {
      showCopyError(error, 'Erro ao copiar relatório.');
    } finally {
      setIsCopyingOutput(false);
    }
  };

  const waitForImagesToLoad = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));

    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
      })
    );
  };

  const copyPurchasePlanningReportImage = async () => {
    if (isCopyingOutput) return;

    const rows = getRowsToCopy();

    if (rows.length === 0) {
      showNoRowsError();
      return;
    }

    setIsCopyingOutput(true);
    setCopiedOutput(null);

    const html = buildPurchasePlanningReportHtml(rows);

    const wrapper = document.createElement('div');

    wrapper.style.position = 'fixed';
    wrapper.style.left = '-1200px';
    wrapper.style.top = '0';
    wrapper.style.width = '860px';
    wrapper.style.background = '#FFFFFF';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '999999';

    wrapper.innerHTML = html;

    document.body.appendChild(wrapper);

    const reportNode = wrapper.firstElementChild;

    try {
      await waitForImagesToLoad(reportNode);

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 200));

      const blob = await toBlob(reportNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
        width: reportNode.scrollWidth,
        height: reportNode.scrollHeight,
        style: {
          margin: '0',
          transform: 'none',
        },
      });

      if (!blob) {
        throw new Error('Não foi possível gerar a imagem do relatório.');
      }

      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'image/png': blob,
        });

        await navigator.clipboard.write([clipboardItem]);

        showCopiedFeedback('report_image');
      } else {
        throw new Error('Seu navegador não suporta copiar imagem para a área de transferência.');
      }
    } catch (error) {
      showCopyError(error, 'Erro ao copiar relatório em imagem.');
    } finally {
      document.body.removeChild(wrapper);
      setIsCopyingOutput(false);
    }
  };

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
      <div className="bg-blueLight items-center p-4 flex flex-row justify-between" style={{ borderRadius: '12px' }}>
        <div className="flex flex-row items-center gap-3">
          <ShoppingCart size={20} className="text-tealBase" strokeWidth={1.75} />
          <span className="text-blueDarker text-xl font-semibold">Planejamento de compras</span>
        </div>
      </div>

      <p className="text-xs text-blueSteel -mt-2">
        Ativos recomendados na carteira do cliente que ainda não foram comprados ou estão abaixo do ideal.
      </p>

      <div className="grid grid-cols-1 gap-5">
        <SimpleInfo title="Valor total a comprar" value={summaries.totalPurchaseValue} />
      </div>

      <div className="px-2 pt-1 pb-2">
        <div
          className="grid items-center gap-4 text-blueDarker text-sm font-semibold px-3 py-2"
          style={{ gridTemplateColumns: GRID }}
        >
          <CustomCheckbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />

          <div>Ticker</div>
          <div>Tipo</div>
          <div>Pr Mercado</div>
          <div>Saldo Ideal</div>
          <div>Total Compra</div>
          <div>Qtd Compra</div>
        </div>

        <div className="space-y-0 mt-1">
          {planningResults.map((position, idx) => (
            <PurchaseRow
              key={position.id}
              position={position}
              idx={idx}
              isSelected={selectedIds.includes(position.id)}
              onToggle={toggleOne}
              onQuantityToBuyChange={handleQuantityToBuyChange}
            />
          ))}
        </div>
      </div>

      <div className="bg-blueLight items-center p-4 flex flex-row justify-between" style={{ borderRadius: '12px' }}>
        <div className="flex flex-row items-center gap-3">
          <Upload size={20} className="text-tealBase" strokeWidth={1.75} />
          <span className="text-blueDarker text-xl font-semibold">Exportar</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copyPurchaseOrderTable}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR TABELA
            </CustomButton>

            <CopiedTooltip visible={copiedOutput === 'table'} message="Tabela copiada para a área de transferência!" />
          </div>

          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copyPurchasePlanningReport}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR RELATÓRIO (TEXTO)
            </CustomButton>

            <CopiedTooltip
              visible={copiedOutput === 'report_text'}
              message="Relatório copiado para a área de transferência!"
            />
          </div>

          <div className="relative">
            <CustomButton
              disabled={isCopyingOutput}
              onClick={copyPurchasePlanningReportImage}
              icon={Copy}
              className={isCopyingOutput ? 'opacity-60 cursor-not-allowed' : ''}
            >
              COPIAR RELATÓRIO (IMAGEM)
            </CustomButton>

            <CopiedTooltip
              visible={copiedOutput === 'report_image'}
              message="Imagem copiada para a área de transferência!"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasePlanning;
